import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2, Clock, CheckCircle, Send, Users, X, Truck, Search, FileText, PenLine, DollarSign } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { WeeklyTimesheetGrid } from '@/components/timesheets/WeeklyTimesheetGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TimesheetDetail() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [timesheet, setTimesheet] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [crafts, setCrafts] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [internalEquipment, setInternalEquipment] = useState([]);
  const [equipmentEntries, setEquipmentEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [movingEntry, setMovingEntry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Customer config cache: { customerId: configObj }
  const [customerConfigs, setCustomerConfigs] = useState({});
  // PO balance cache: { jobId: [poData] }
  const [jobPOBalances, setJobPOBalances] = useState({});
  // Timesheet-level fields (work descriptions per job, signatures)
  const [workDescriptions, setWorkDescriptions] = useState([]);
  const [submittedByPrint, setSubmittedByPrint] = useState('');
  const [approvedByPrint, setApprovedByPrint] = useState('');
  const [savingFields, setSavingFields] = useState(false);
  
  // Equipment entry state
  const [equipDialogOpen, setEquipDialogOpen] = useState(false);
  const [editingEquipEntry, setEditingEquipEntry] = useState(null);
  const [deleteEquipDialogOpen, setDeleteEquipDialogOpen] = useState(false);
  const [deletingEquipEntry, setDeletingEquipEntry] = useState(null);
  const [equipFormData, setEquipFormData] = useState({
    equipmentId: '',
    jobId: '',
    hours: '',
    hourlyRate: '',
    notes: ''
  });
  const [equipSubmitting, setEquipSubmitting] = useState(false);
  
  // Form data for crew member selection
  const [formData, setFormData] = useState({
    crewMemberId: '',
    craftId: '',
    classificationId: '',
    notes: ''
  });
  
  // Bulk add: per-person allocations (each crew member gets own job/class/hours row)
  const [crewAllocations, setCrewAllocations] = useState([]);
  const [crewSearchFilter, setCrewSearchFilter] = useState('');
  
  // Single job allocation for editing
  const [editFormData, setEditFormData] = useState({
    jobId: '',
    crewMemberId: '',
    craftId: '',
    classificationId: '',
    hoursST: '',
    hoursOT: '',
    hoursDT: '',
    notes: ''
  });
  
  const [moveData, setMoveData] = useState({
    newJobId: '',
    reason: ''
  });
  
  const isForeman = user?.role === 'FOREMAN';
  const isOffice = user?.role === 'OFFICE' || user?.role === 'OWNER';
  const isOwner = user?.role === 'OWNER';
  const isDraft = timesheet?.status === 'DRAFT';
  const isSubmitted = timesheet?.status === 'SUBMITTED';
  // Owner and Office can always edit, Foreman can edit only draft timesheets
  const canEdit = (isForeman && isDraft) || isOffice || isOwner;
  
  useEffect(() => {
    fetchData();
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch PO balances for existing labor entry jobs when timesheet loads
  useEffect(() => {
    if (!Object.keys(customerConfigs).length || !jobs.length) return;
    for (const job of jobs) {
      const cfg = customerConfigs[job.customerId];
      if (cfg?.trackPOBalance) {
        fetchJobPOBalance(job.id);
      }
    }
  }, [customerConfigs, jobs]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const fetchData = async () => {
    try {
      const [timesheetRes, jobsRes, crewRes, craftsRes, classRes, equipRes, equipEntriesRes, customersRes] = await Promise.all([
        axios.get(`${API}/timesheets/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crew-members`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crafts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/classifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/internal-equipment`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/timesheets/${id}/equipment-entries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setTimesheet(timesheetRes.data);
      setWorkDescriptions(timesheetRes.data.workDescriptions || []);
      setSubmittedByPrint(timesheetRes.data.submittedByPrint || '');
      setApprovedByPrint(timesheetRes.data.approvedByPrint || '');
      setJobs(jobsRes.data);
      setCrewMembers(crewRes.data.filter(c => c.active));
      setCrafts(craftsRes.data);
      setClassifications(classRes.data);
      setInternalEquipment(equipRes.data.filter(e => e.active !== false));
      setEquipmentEntries(equipEntriesRes.data);
      
      // Build customer config cache
      const configs = {};
      for (const cust of customersRes.data) {
        if (cust.config && Object.values(cust.config).some(v => v === true)) {
          configs[cust.id] = cust.config;
        }
      }
      setCustomerConfigs(configs);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load timesheet');
      navigate('/timesheets');
    } finally {
      setLoading(false);
    }
  };
  
  const getJobInfo = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? `${job.jobNumber} - ${job.description}` : jobId;
  };
  
  const getCrewMemberName = (crewId) => {
    const member = crewMembers.find(c => c.id === crewId);
    return member?.fullName || crewId;
  };
  
  const getCraftName = (craftId) => {
    const craft = crafts.find(c => c.id === craftId);
    return craft?.name || craftId;
  };
  
  const getClassificationName = (classId) => {
    const cls = classifications.find(c => c.id === classId);
    return cls?.name || classId;
  };
  
  const getFilteredClassifications = (craftId) => {
    return classifications.filter(c => c.craftId === craftId);
  };
  
  const getEligibleCrewMembers = (craftId) => {
    if (!craftId) return crewMembers;
    return crewMembers.filter(c => c.eligibleCraftIds?.includes(craftId));
  };

  // Get customer config for a job (returns null if no special config)
  const getJobCustomerConfig = (jobId) => {
    if (!jobId) return null;
    const job = jobs.find(j => j.id === jobId);
    if (!job) return null;
    return customerConfigs[job.customerId] || null;
  };

  // Fetch PO balances for a job  
  const fetchJobPOBalance = async (jobId) => {
    if (!jobId || jobPOBalances[jobId]) return;
    try {
      const res = await axios.get(`${API}/purchase-orders/by-job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobPOBalances(prev => ({ ...prev, [jobId]: res.data }));
    } catch {
      // silently fail - PO balance is supplementary
    }
  };

  // Save timesheet-level fields (work description, signatures)
  const saveTimesheetFields = async (fieldUpdate) => {
    setSavingFields(true);
    try {
      await axios.patch(`${API}/timesheets/${id}`, fieldUpdate, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to save field:', error);
    } finally {
      setSavingFields(false);
    }
  };

  // Get/set work description for a specific job
  const getJobWorkDescription = (jobId) => {
    const entry = workDescriptions.find(wd => wd.jobId === jobId);
    return entry?.description || '';
  };

  const updateJobWorkDescription = (jobId, description) => {
    setWorkDescriptions(prev => {
      const existing = prev.find(wd => wd.jobId === jobId);
      if (existing) {
        return prev.map(wd => wd.jobId === jobId ? { ...wd, description } : wd);
      }
      return [...prev, { jobId, description }];
    });
  };

  const saveAllWorkDescriptions = () => {
    saveTimesheetFields({ workDescriptions });
  };

  // Get jobs relevant to this timesheet (from labor entries + any with saved descriptions)
  const getTimesheetJobs = () => {
    const jobIds = new Set();
    if (timesheet?.laborEntries?.length) {
      timesheet.laborEntries.forEach(e => jobIds.add(e.jobId));
    }
    workDescriptions.forEach(wd => jobIds.add(wd.jobId));
    return jobs.filter(j => jobIds.has(j.id));
  };

  // Check if this timesheet should show T&M sections (Work Description, Signatures, PO Balance)
  // Shows when any customer in the company has useDetailedTM enabled
  const getTimesheetCustomerConfig = () => {
    // First check if any entry's job is from a configured customer
    if (timesheet?.laborEntries?.length) {
      for (const entry of timesheet.laborEntries) {
        const cfg = getJobCustomerConfig(entry.jobId);
        if (cfg && cfg.useDetailedTM) return cfg;
      }
    }
    // Fall back to checking if any customer in the company has config enabled
    for (const custId of Object.keys(customerConfigs)) {
      if (customerConfigs[custId]?.useDetailedTM) return customerConfigs[custId];
    }
    return null;
  };

  const addCrewMemberAllocation = (crewMemberId) => {
    if (!crewMemberId) return;
    if (crewAllocations.some(a => a.crewMemberId === crewMemberId)) return;
    setCrewAllocations(prev => [...prev, {
      crewMemberId,
      craftId: formData.craftId,
      jobs: [{ jobId: '', classificationId: '', hoursST: '8', hoursOT: '0', hoursDT: '0' }]
    }]);
  };

  const removeCrewMemberAllocation = (crewMemberId) => {
    setCrewAllocations(prev => prev.filter(a => a.crewMemberId !== crewMemberId));
  };

  const toggleCrewMember = (crewMemberId) => {
    if (crewAllocations.some(a => a.crewMemberId === crewMemberId)) {
      removeCrewMemberAllocation(crewMemberId);
    } else {
      addCrewMemberAllocation(crewMemberId);
    }
  };

  const addAllEligibleCrew = () => {
    const currentCraftId = formData.craftId;
    const eligible = getEligibleCrewMembers(currentCraftId);
    const existingIds = new Set(crewAllocations.map(a => a.crewMemberId));
    const newAllocations = eligible
      .filter(m => !existingIds.has(m.id))
      .map(m => ({
        crewMemberId: m.id,
        craftId: currentCraftId,
        jobs: [{ jobId: '', classificationId: '', hoursST: '8', hoursOT: '0', hoursDT: '0' }]
      }));
    setCrewAllocations(prev => [...prev, ...newAllocations]);
  };

  const removeCrewAllocation = (index) => {
    setCrewAllocations(prev => prev.filter((_, i) => i !== index));
  };

  const addJobRow = (allocIndex) => {
    setCrewAllocations(prev => {
      const updated = [...prev];
      updated[allocIndex] = {
        ...updated[allocIndex],
        jobs: [...updated[allocIndex].jobs, { jobId: '', classificationId: '', hoursST: '0', hoursOT: '0', hoursDT: '0' }]
      };
      return updated;
    });
  };

  const removeJobRow = (allocIndex, jobIndex) => {
    setCrewAllocations(prev => {
      const updated = [...prev];
      const newJobs = updated[allocIndex].jobs.filter((_, ji) => ji !== jobIndex);
      if (newJobs.length === 0) {
        return prev.filter((_, i) => i !== allocIndex);
      }
      updated[allocIndex] = { ...updated[allocIndex], jobs: newJobs };
      return updated;
    });
  };

  const updateJobRow = (allocIndex, jobIndex, field, value) => {
    setCrewAllocations(prev => {
      const updated = [...prev];
      const newJobs = [...updated[allocIndex].jobs];
      newJobs[jobIndex] = { ...newJobs[jobIndex], [field]: value };
      updated[allocIndex] = { ...updated[allocIndex], jobs: newJobs };
      return updated;
    });
    // Fetch PO balance when job changes
    if (field === 'jobId' && value) {
      const jobConfig = getJobCustomerConfig(value);
      if (jobConfig?.trackPOBalance) {
        fetchJobPOBalance(value);
      }
    }
  };

  const applyToAllAllocations = (field, value) => {
    setCrewAllocations(prev => prev.map(a => ({
      ...a,
      jobs: a.jobs.map(j => ({ ...j, [field]: value }))
    })));
  };
  
  const handleOpenDialog = (entry = null) => {
    if (entry) {
      // Editing existing entry - use single form
      setEditingEntry(entry);
      setEditFormData({
        jobId: entry.jobId,
        crewMemberId: entry.crewMemberId,
        craftId: entry.craftId,
        classificationId: entry.classificationId,
        hoursST: entry.hoursST.toString(),
        hoursOT: entry.hoursOT.toString(),
        hoursDT: entry.hoursDT.toString(),
        notes: entry.notes || ''
      });
    } else {
      // Adding new entries - use multi-job form with bulk crew selection
      setEditingEntry(null);
      setFormData({
        crewMemberId: '',
        craftId: '',
        classificationId: '',
        notes: ''
      });
      setCrewAllocations([]);
      setCrewSearchFilter('');
    }
    setDialogOpen(true);
  };
  
  const handleCraftChange = (craftId) => {
    if (editingEntry) {
      setEditFormData({
        ...editFormData,
        craftId,
        classificationId: '',
        crewMemberId: ''
      });
    } else {
      setFormData({
        ...formData,
        craftId,
        classificationId: '',
        crewMemberId: ''
      });
    }
  };
  
  const getTotalHours = () => {
    return crewAllocations.reduce((sum, alloc) => {
      return sum + alloc.jobs.reduce((jSum, j) => {
        return jSum + (parseFloat(j.hoursST) || 0) + (parseFloat(j.hoursOT) || 0) + (parseFloat(j.hoursDT) || 0);
      }, 0);
    }, 0);
  };
  
  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (editingEntry) {
        // Update single entry
        const payload = {
          ...editFormData,
          hoursST: parseFloat(editFormData.hoursST) || 0,
          hoursOT: parseFloat(editFormData.hoursOT) || 0,
          hoursDT: parseFloat(editFormData.hoursDT) || 0
        };
        
        await axios.patch(`${API}/labor-entries/${editingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Labor entry updated');
      } else {
        // Create entries from crew allocations (per-person, multi-job model)
        const allEntries = [];
        for (const alloc of crewAllocations) {
          for (const job of alloc.jobs) {
            const totalHrs = (parseFloat(job.hoursST) || 0) + (parseFloat(job.hoursOT) || 0) + (parseFloat(job.hoursDT) || 0);
            if (alloc.crewMemberId && job.jobId && job.classificationId && totalHrs > 0) {
              allEntries.push({
                jobId: job.jobId,
                crewMemberId: alloc.crewMemberId,
                craftId: alloc.craftId,
                classificationId: job.classificationId,
                hoursST: parseFloat(job.hoursST) || 0,
                hoursOT: parseFloat(job.hoursOT) || 0,
                hoursDT: parseFloat(job.hoursDT) || 0,
                notes: formData.notes
              });
            }
          }
        }
        
        if (allEntries.length === 0) {
          toast.error('Please add at least one crew member with job, classification, and hours');
          setSubmitting(false);
          return;
        }
        
        for (const payload of allEntries) {
          await axios.post(`${API}/timesheets/${id}/labor-entries`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        
        toast.success(`${allEntries.length} labor ${allEntries.length === 1 ? 'entry' : 'entries'} added`);
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save labor entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to save labor entry');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDeleteEntry = async () => {
    if (!deletingEntry) return;
    
    try {
      await axios.delete(`${API}/labor-entries/${deletingEntry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Labor entry deleted');
      setDeleteDialogOpen(false);
      setDeletingEntry(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete labor entry:', error);
      toast.error('Failed to delete labor entry');
    }
  };
  
  const handleMoveEntry = async () => {
    if (!movingEntry || !moveData.newJobId || !moveData.reason) return;
    
    try {
      await axios.post(`${API}/labor-entries/${movingEntry.id}/move`, moveData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Labor entry moved');
      setMoveDialogOpen(false);
      setMovingEntry(null);
      setMoveData({ newJobId: '', reason: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to move labor entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to move labor entry');
    }
  };
  
  const handleSubmitTimesheet = async () => {
    try {
      await axios.post(`${API}/timesheets/${id}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Timesheet submitted for approval');
      setSubmitDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to submit timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit timesheet');
    }
  };
  
  const handleApproveTimesheet = async () => {
    try {
      await axios.post(`${API}/timesheets/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Timesheet approved');
      setApproveDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to approve timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve timesheet');
    }
  };

  // Equipment entry handlers
  const openEquipDialog = (entry = null) => {
    if (entry) {
      setEditingEquipEntry(entry);
      setEquipFormData({
        equipmentId: entry.equipmentId,
        jobId: entry.jobId,
        hours: entry.hours.toString(),
        hourlyRate: entry.hourlyRate?.toString() || '',
        notes: entry.notes || ''
      });
    } else {
      setEditingEquipEntry(null);
      setEquipFormData({
        equipmentId: '',
        jobId: '',
        hours: '',
        hourlyRate: '',
        notes: ''
      });
    }
    setEquipDialogOpen(true);
  };

  const handleEquipSubmit = async (e) => {
    e.preventDefault();
    setEquipSubmitting(true);

    try {
      const payload = {
        equipmentId: equipFormData.equipmentId,
        jobId: equipFormData.jobId,
        hours: parseFloat(equipFormData.hours),
        hourlyRate: equipFormData.hourlyRate ? parseFloat(equipFormData.hourlyRate) : null,
        notes: equipFormData.notes || null
      };

      if (editingEquipEntry) {
        await axios.put(`${API}/equipment-entries/${editingEquipEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Equipment entry updated');
      } else {
        await axios.post(`${API}/timesheets/${id}/equipment-entries`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Equipment entry added');
      }
      setEquipDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save equipment entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to save equipment entry');
    } finally {
      setEquipSubmitting(false);
    }
  };

  const handleDeleteEquipEntry = async () => {
    if (!deletingEquipEntry) return;

    try {
      await axios.delete(`${API}/equipment-entries/${deletingEquipEntry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Equipment entry deleted');
      setDeleteEquipDialogOpen(false);
      setDeletingEquipEntry(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete equipment entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete equipment entry');
    }
  };

  const getEquipmentName = (equipId) => {
    const equip = internalEquipment.find(e => e.id === equipId);
    return equip?.name || equipId;
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'DRAFT':
        return <Badge className='bg-slate-100 text-slate-800 border-slate-200 rounded-sm'>Draft</Badge>;
      case 'SUBMITTED':
        return <Badge className='bg-amber-100 text-amber-800 border-amber-200 rounded-sm'>Submitted</Badge>;
      case 'APPROVED':
        return <Badge className='bg-green-100 text-green-800 border-green-200 rounded-sm'>Approved</Badge>;
      default:
        return <Badge className='bg-slate-100 text-slate-800 rounded-sm'>{status}</Badge>;
    }
  };
  
  if (loading) {
    return (
      <div className='p-4 md:p-8'>
        <div className='text-center text-slate-600'>Loading timesheet...</div>
      </div>
    );
  }
  
  if (!timesheet) {
    return (
      <div className='p-4 md:p-8'>
        <div className='text-center text-slate-600'>Timesheet not found</div>
      </div>
    );
  }
  
  const laborEntries = timesheet.laborEntries || [];
  const totalHours = laborEntries.reduce((sum, e) => sum + e.hoursST + e.hoursOT + e.hoursDT, 0);
  const totalLaborCharges = laborEntries.reduce((sum, e) => sum + e.totalCharge, 0);
  const totalEquipmentHours = equipmentEntries.reduce((sum, e) => sum + e.hours, 0);
  const totalEquipmentCharges = equipmentEntries.reduce((sum, e) => sum + e.totalCharge, 0);
  const totalCharges = totalLaborCharges + totalEquipmentCharges;
  
  // Get current form data based on edit mode
  const currentCraftId = editingEntry ? editFormData.craftId : formData.craftId;
  const currentCrewMemberId = editingEntry ? editFormData.crewMemberId : formData.crewMemberId;
  const currentClassificationId = editingEntry ? editFormData.classificationId : formData.classificationId;
  
  return (
    <div className='p-4 md:p-8' data-testid='timesheet-detail-page'>
      <Button
        onClick={() => navigate('/timesheets')}
        variant='ghost'
        className='mb-4 text-slate-600 hover:text-[#1A3668] rounded-sm'
      >
        <ArrowLeft className='w-4 h-4 mr-2' />
        Back to Timesheets
      </Button>
      
      {/* Header */}
      <div className='flex flex-col gap-4 mb-6'>
        <div>
          <div className='flex flex-wrap items-center gap-2 md:gap-3 mb-2'>
            <h1 className='text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900'>
              {timesheet.type === 'WEEKLY' 
                ? `Week of ${timesheet.date}` 
                : timesheet.date}
            </h1>
            {timesheet.type === 'WEEKLY' && (
              <span className='text-sm font-bold bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-sm'>WEEKLY</span>
            )}
            {getStatusBadge(timesheet.status)}
          </div>
          <p className='text-base md:text-lg text-slate-700'>
            {timesheet.shift} Shift
          </p>
        </div>
        <div className='flex flex-col sm:flex-row gap-2'>
          {isDraft && laborEntries.length > 0 && (isForeman || isOffice || isOwner) && (
            <Button
              onClick={() => setSubmitDialogOpen(true)}
              data-testid='submit-timesheet-button'
              className='bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-sm py-3 md:py-2 active:scale-[0.98]'
            >
              <Send className='w-4 h-4 mr-2' />
              Submit for Approval
            </Button>
          )}
          {(isOffice || isOwner) && isSubmitted && (
            <Button
              onClick={() => setApproveDialogOpen(true)}
              data-testid='approve-timesheet-button'
              className='bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm py-3 md:py-2 active:scale-[0.98]'
            >
              <CheckCircle className='w-4 h-4 mr-2' />
              Approve
            </Button>
          )}
          {canEdit && timesheet.type !== 'WEEKLY' && (
            <Button
              onClick={() => handleOpenDialog()}
              data-testid='add-labor-entry-button'
              className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm py-3 md:py-2 active:scale-[0.98]'
            >
              <Plus className='w-4 h-4 mr-2' />
              Add Labor Entry
            </Button>
          )}
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className='grid grid-cols-2 gap-3 md:gap-4 mb-6'>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Labor Entries</p>
            <p className='text-3xl font-bold text-[#1A3668] mt-2'>{laborEntries.length}</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Equipment Entries</p>
            <p className='text-3xl font-bold text-amber-600 mt-2'>{equipmentEntries.length}</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Total Hours</p>
            <p className='text-3xl font-bold text-slate-700 mt-2'>{(totalHours + totalEquipmentHours).toFixed(1)}</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Total Charges</p>
            <p className='text-3xl font-bold text-[#16A34A] mt-2'>{formatCurrency(totalCharges)}</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Labor Entries */}
      {timesheet.type === 'WEEKLY' ? (
        <WeeklyTimesheetGrid
          timesheet={timesheet}
          laborEntries={laborEntries}
          jobs={jobs}
          crewMembers={crewMembers}
          crafts={crafts}
          classifications={classifications}
          canEdit={canEdit}
          token={token}
          onRefresh={fetchData}
          getFilteredClassifications={getFilteredClassifications}
        />
      ) : (
      <>
      {/* Daily Labor Entries - Mobile Cards / Desktop Table */}
      {laborEntries.length === 0 ? (
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-8 md:p-12 text-center'>
            <Users className='w-10 h-10 md:w-12 md:h-12 mx-auto text-slate-400 mb-4' />
            <p className='text-slate-600 mb-4'>No labor entries yet</p>
            {canEdit && (
              <Button
                onClick={() => handleOpenDialog()}
                variant='outline'
                className='border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm py-3'
              >
                Add your first labor entry
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className='md:hidden space-y-3'>
            {laborEntries.map((entry) => (
              <Card key={entry.id} data-testid={`labor-entry-card-${entry.id}`} className='border-slate-200 shadow-sm rounded-sm'>
                <CardContent className='p-4'>
                  <div className='flex justify-between items-start mb-3'>
                    <div>
                      <p className='font-bold text-[#1A3668] text-sm'>{getJobInfo(entry.jobId)}</p>
                      <p className='font-medium text-slate-900'>{getCrewMemberName(entry.crewMemberId)}</p>
                      <p className='text-sm text-slate-500'>{getCraftName(entry.craftId)} - {getClassificationName(entry.classificationId)}</p>
                    </div>
                    {entry.shiftMultiplier && entry.shiftMultiplier !== 1.0 ? (
                      <Badge className='rounded-sm bg-indigo-100 text-indigo-700 border-indigo-200 text-xs'>
                        {entry.shift} ({entry.shiftMultiplier.toFixed(2)}x)
                      </Badge>
                    ) : null}
                  </div>
                  <div className='grid grid-cols-4 gap-2 text-center mb-3 bg-slate-50 rounded-sm p-2'>
                    <div>
                      <p className='text-xs text-slate-500'>ST</p>
                      <p className='font-mono font-bold'>{entry.hoursST.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className='text-xs text-slate-500'>OT</p>
                      <p className='font-mono font-bold text-amber-600'>{entry.hoursOT.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className='text-xs text-slate-500'>DT</p>
                      <p className='font-mono font-bold text-red-600'>{entry.hoursDT.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className='text-xs text-slate-500'>Total</p>
                      <p className='font-mono font-bold text-[#16A34A]'>{formatCurrency(entry.totalCharge)}</p>
                    </div>
                  </div>
                  {canEdit && (
                    <div className='flex gap-2'>
                      <Button
                        onClick={() => handleOpenDialog(entry)}
                        data-testid={`edit-entry-${entry.id}`}
                        variant='outline'
                        size='sm'
                        className='flex-1 border-2 border-slate-200 hover:border-[#1A3668] rounded-sm'
                      >
                        <Pencil className='w-3 h-3 mr-1' />
                        Edit
                      </Button>
                      {(isOffice || isOwner) && (
                        <Button
                          onClick={() => {
                            setMovingEntry(entry);
                            setMoveDialogOpen(true);
                          }}
                          data-testid={`move-entry-${entry.id}`}
                          variant='outline'
                          size='sm'
                          className='border-2 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 rounded-sm'
                        >
                          <ArrowLeft className='w-3 h-3 rotate-180' />
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          setDeletingEntry(entry);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-entry-${entry.id}`}
                        variant='outline'
                        size='sm'
                        className='border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm'
                      >
                        <Trash2 className='w-3 h-3' />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className='hidden md:block overflow-x-auto'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='bg-slate-100 border-b-2 border-slate-200'>
                  <th className='text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Job</th>
                  <th className='text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Crew Member</th>
                  <th className='text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Craft</th>
                  <th className='text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Classification</th>
                  <th className='text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>ST Hrs</th>
                  <th className='text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>OT Hrs</th>
                  <th className='text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>DT Hrs</th>
                  <th className='text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Total</th>
                  <th className='text-center p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Shift</th>
                  {canEdit && <th className='text-center p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {laborEntries.map((entry) => (
                  <tr key={entry.id} data-testid={`labor-entry-row-${entry.id}`} className='border-b border-slate-200 hover:bg-slate-50'>
                    <td className='p-4 text-sm font-medium text-slate-700'>{getJobInfo(entry.jobId)}</td>
                    <td className='p-4 text-sm font-medium text-slate-700'>{getCrewMemberName(entry.crewMemberId)}</td>
                    <td className='p-4 text-sm text-slate-600'>{getCraftName(entry.craftId)}</td>
                    <td className='p-4 text-sm text-slate-600'>{getClassificationName(entry.classificationId)}</td>
                    <td className='p-4 text-right text-sm font-mono text-slate-700'>{entry.hoursST.toFixed(1)}</td>
                    <td className='p-4 text-right text-sm font-mono text-amber-600'>{entry.hoursOT.toFixed(1)}</td>
                    <td className='p-4 text-right text-sm font-mono text-red-600'>{entry.hoursDT.toFixed(1)}</td>
                    <td className='p-4 text-right text-sm font-mono font-bold text-[#16A34A]'>{formatCurrency(entry.totalCharge)}</td>
                    <td className='p-4 text-center'>
                      {entry.shiftMultiplier && entry.shiftMultiplier !== 1.0 ? (
                        <Badge className='rounded-sm bg-indigo-100 text-indigo-700 border-indigo-200'>
                          {entry.shift} ({entry.shiftMultiplier.toFixed(2)}x)
                        </Badge>
                      ) : (
                        <span className='text-sm text-slate-500'>{entry.shift || timesheet.shift}</span>
                      )}
                    </td>
                    {canEdit && (
                      <td className='p-4 text-center'>
                        <div className='flex gap-2 justify-center'>
                          <Button
                            onClick={() => handleOpenDialog(entry)}
                            data-testid={`edit-entry-${entry.id}`}
                            variant='outline'
                            size='sm'
                            className='border-2 border-slate-200 hover:border-[#1A3668] rounded-sm'
                          >
                            <Pencil className='w-3 h-3' />
                          </Button>
                          {(isOffice || isOwner) && (
                            <Button
                              onClick={() => {
                                setMovingEntry(entry);
                                setMoveDialogOpen(true);
                              }}
                              data-testid={`move-entry-${entry.id}`}
                              variant='outline'
                              size='sm'
                              className='border-2 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 rounded-sm'
                              title='Move to another job'
                            >
                              <ArrowLeft className='w-3 h-3 rotate-180' />
                            </Button>
                          )}
                          <Button
                            onClick={() => {
                              setDeletingEntry(entry);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-entry-${entry.id}`}
                            variant='outline'
                            size='sm'
                            className='border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm'
                          >
                            <Trash2 className='w-3 h-3' />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>
      )}

      {/* Internal Equipment Entries Section */}
      <div className='mt-6 md:mt-8'>
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4'>
          <h2 className='text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2'>
            <Truck className='w-5 h-5 text-amber-600' />
            Internal Equipment Entries
          </h2>
          {canEdit && (
            <Button
              onClick={() => openEquipDialog()}
              data-testid='add-equipment-entry-button'
              className='bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-sm py-3 md:py-2 active:scale-[0.98]'
            >
              <Plus className='w-4 h-4 mr-2' />
              Add Equipment
            </Button>
          )}
        </div>

        {equipmentEntries.length === 0 ? (
          <Card className='border-slate-200 shadow-sm rounded-sm'>
            <CardContent className='p-6 md:p-8 text-center'>
              <Truck className='w-10 h-10 mx-auto text-slate-400 mb-3' />
              <p className='text-slate-600 mb-3'>No equipment entries yet</p>
              {canEdit && (
                <Button
                  onClick={() => openEquipDialog()}
                  variant='outline'
                  className='border-2 border-amber-600 text-amber-600 hover:bg-amber-50 rounded-sm py-3'
                >
                  Add equipment usage
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className='md:hidden space-y-3'>
              {equipmentEntries.map((entry) => (
                <Card key={entry.id} data-testid={`equipment-entry-card-${entry.id}`} className='border-amber-100 shadow-sm rounded-sm bg-amber-50/50'>
                  <CardContent className='p-4'>
                    <div className='flex justify-between items-start mb-3'>
                      <div>
                        <p className='font-bold text-slate-900'>{entry.equipmentName}</p>
                        <p className='text-sm text-[#1A3668]'>{entry.jobNumber}</p>
                      </div>
                      <p className='font-bold font-mono text-[#16A34A]'>{formatCurrency(entry.totalCharge)}</p>
                    </div>
                    <div className='grid grid-cols-2 gap-2 text-sm mb-3'>
                      <div>
                        <span className='text-slate-500'>Hours: </span>
                        <span className='font-mono font-bold'>{entry.hours.toFixed(1)}</span>
                      </div>
                      <div>
                        <span className='text-slate-500'>Rate: </span>
                        <span className='font-mono'>${entry.hourlyRate.toFixed(2)}/hr</span>
                      </div>
                    </div>
                    {entry.notes && <p className='text-sm text-slate-600 mb-3'>{entry.notes}</p>}
                    {canEdit && (
                      <div className='flex gap-2'>
                        <Button
                          onClick={() => openEquipDialog(entry)}
                          data-testid={`edit-equipment-entry-${entry.id}`}
                          variant='outline'
                          size='sm'
                          className='flex-1 border-2 border-slate-200 hover:border-[#1A3668] rounded-sm'
                        >
                          <Pencil className='w-3 h-3 mr-1' />
                          Edit
                        </Button>
                        <Button
                          onClick={() => {
                            setDeletingEquipEntry(entry);
                            setDeleteEquipDialogOpen(true);
                          }}
                          data-testid={`delete-equipment-entry-${entry.id}`}
                          variant='outline'
                          size='sm'
                          className='border-2 border-red-200 text-red-600 hover:bg-red-50 rounded-sm'
                        >
                          <Trash2 className='w-3 h-3' />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className='hidden md:block overflow-x-auto'>
              <table className='w-full border-collapse'>
                <thead>
                  <tr className='bg-amber-50 border-b-2 border-amber-200'>
                    <th className='text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Equipment</th>
                    <th className='text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Job</th>
                    <th className='text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Hours</th>
                    <th className='text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Rate</th>
                    <th className='text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Total</th>
                    <th className='text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Notes</th>
                    {canEdit && <th className='text-center p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {equipmentEntries.map((entry) => (
                    <tr key={entry.id} data-testid={`equipment-entry-row-${entry.id}`} className='border-b border-slate-200 hover:bg-slate-50'>
                      <td className='p-4 font-bold text-slate-900'>{entry.equipmentName}</td>
                      <td className='p-4 text-sm text-slate-700'>{entry.jobNumber}</td>
                      <td className='p-4 text-right font-mono'>{entry.hours.toFixed(1)}</td>
                      <td className='p-4 text-right font-mono text-slate-600'>${entry.hourlyRate.toFixed(2)}/hr</td>
                      <td className='p-4 text-right font-bold font-mono text-[#16A34A]'>{formatCurrency(entry.totalCharge)}</td>
                      <td className='p-4 text-sm text-slate-600'>{entry.notes || '-'}</td>
                      {canEdit && (
                        <td className='p-4'>
                          <div className='flex gap-1 justify-center'>
                            <Button
                              onClick={() => openEquipDialog(entry)}
                              data-testid={`edit-equipment-entry-${entry.id}`}
                              variant='outline'
                              size='sm'
                              className='border-2 border-slate-200 hover:border-[#1A3668] rounded-sm'
                            >
                              <Pencil className='w-3 h-3' />
                            </Button>
                            <Button
                              onClick={() => {
                                setDeletingEquipEntry(entry);
                                setDeleteEquipDialogOpen(true);
                              }}
                              data-testid={`delete-equipment-entry-${entry.id}`}
                              variant='outline'
                              size='sm'
                              className='border-2 border-red-200 text-red-600 hover:bg-red-50 rounded-sm'
                            >
                              <Trash2 className='w-3 h-3' />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Work Description Section (per-job, only for configured clients) */}
      {getTimesheetCustomerConfig() && (
        <div className='mt-6 md:mt-8' data-testid='work-description-section'>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2'>
              <FileText className='w-5 h-5 text-blue-600' />
              Work Description
            </h2>
            {canEdit && (
              <Select onValueChange={(jobId) => {
                if (!workDescriptions.find(wd => wd.jobId === jobId)) {
                  const updated = [...workDescriptions, { jobId, description: '' }];
                  setWorkDescriptions(updated);
                }
              }}>
                <SelectTrigger data-testid='add-work-desc-job' className='w-[200px] rounded-sm border-2 h-9 text-xs'>
                  <SelectValue placeholder='Add job...' />
                </SelectTrigger>
                <SelectContent>
                  {jobs.filter(j => !workDescriptions.find(wd => wd.jobId === j.id)).map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.jobNumber}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          {(() => {
            const relevantJobs = getTimesheetJobs();
            if (relevantJobs.length === 0 && workDescriptions.length === 0) {
              return (
                <Card className='border-slate-200 shadow-sm rounded-sm'>
                  <CardContent className='p-6 text-center text-sm text-slate-500'>
                    Add labor entries or select a job above to add work descriptions
                  </CardContent>
                </Card>
              );
            }
            // Show a textarea for each job that has entries or a saved description
            const jobsToShow = [...new Set([
              ...relevantJobs.map(j => j.id),
              ...workDescriptions.map(wd => wd.jobId)
            ])];
            return (
              <div className='space-y-3'>
                {jobsToShow.map(jobId => {
                  const job = jobs.find(j => j.id === jobId);
                  if (!job) return null;
                  return (
                    <Card key={jobId} className='border-slate-200 shadow-sm rounded-sm'>
                      <CardContent className='p-4'>
                        <div className='flex items-center gap-2 mb-2'>
                          <span className='text-sm font-bold text-[#1A3668]'>{job.jobNumber}</span>
                          <span className='text-xs text-slate-500'>{job.description}</span>
                        </div>
                        <textarea
                          data-testid={`work-desc-${jobId}`}
                          value={getJobWorkDescription(jobId)}
                          onChange={(e) => updateJobWorkDescription(jobId, e.target.value)}
                          onBlur={saveAllWorkDescriptions}
                          placeholder={`Describe work performed on ${job.jobNumber}...`}
                          className='w-full min-h-[80px] p-3 border-2 border-slate-200 rounded-sm text-sm resize-y focus:outline-none focus:border-[#1A3668] transition-colors'
                          disabled={!canEdit}
                        />
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Signatures Section (only for configured clients) */}
      {getTimesheetCustomerConfig() && (
        <div className='mt-6 md:mt-8' data-testid='signatures-section'>
          <h2 className='text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2 mb-4'>
            <PenLine className='w-5 h-5 text-slate-600' />
            Signatures
          </h2>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <Card className='border-slate-200 shadow-sm rounded-sm'>
              <CardContent className='p-4 md:p-6'>
                <p className='text-xs font-bold uppercase tracking-wider text-slate-500 mb-3'>Submitted By</p>
                <div className='space-y-3'>
                  <div>
                    <Label className='text-xs font-bold uppercase text-slate-400'>Print Name</Label>
                    <Input
                      data-testid='submitted-by-print'
                      value={submittedByPrint}
                      onChange={(e) => setSubmittedByPrint(e.target.value)}
                      onBlur={() => saveTimesheetFields({ submittedByPrint })}
                      placeholder='Print full name'
                      className='mt-1 rounded-sm border-2'
                      disabled={!canEdit}
                    />
                  </div>
                  <div>
                    <Label className='text-xs font-bold uppercase text-slate-400'>Signature</Label>
                    <div className='mt-1 h-20 border-2 border-slate-200 rounded-sm bg-slate-50 flex items-end justify-center pb-2'>
                      <div className='w-4/5 border-b-2 border-slate-400' />
                    </div>
                    <p className='text-[10px] text-slate-400 mt-1 text-center'>Sign on printed PDF</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className='border-slate-200 shadow-sm rounded-sm'>
              <CardContent className='p-4 md:p-6'>
                <p className='text-xs font-bold uppercase tracking-wider text-slate-500 mb-3'>Approved By</p>
                <div className='space-y-3'>
                  <div>
                    <Label className='text-xs font-bold uppercase text-slate-400'>Print Name</Label>
                    <Input
                      data-testid='approved-by-print'
                      value={approvedByPrint}
                      onChange={(e) => setApprovedByPrint(e.target.value)}
                      onBlur={() => saveTimesheetFields({ approvedByPrint })}
                      placeholder='Print full name'
                      className='mt-1 rounded-sm border-2'
                      disabled={!canEdit}
                    />
                  </div>
                  <div>
                    <Label className='text-xs font-bold uppercase text-slate-400'>Signature</Label>
                    <div className='mt-1 h-20 border-2 border-slate-200 rounded-sm bg-slate-50 flex items-end justify-center pb-2'>
                      <div className='w-4/5 border-b-2 border-slate-400' />
                    </div>
                    <p className='text-[10px] text-slate-400 mt-1 text-center'>Sign on printed PDF</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* PO Balance Summary (only for configured clients with PO tracking) */}
      {getTimesheetCustomerConfig()?.trackPOBalance && (() => {
        // Collect all POs from all jobs with PO tracking
        const allPOs = [];
        const seenPOIds = new Set();
        for (const jid of Object.keys(jobPOBalances)) {
          for (const po of (jobPOBalances[jid] || [])) {
            if (!seenPOIds.has(po.id)) {
              seenPOIds.add(po.id);
              allPOs.push(po);
            }
          }
        }
        if (allPOs.length === 0) return null;
        return (
          <div className='mt-6 md:mt-8' data-testid='po-burn-summary'>
            <h2 className='text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2 mb-4'>
              <DollarSign className='w-5 h-5 text-green-600' />
              PO Balance Summary
            </h2>
            <div className='space-y-4'>
              {allPOs.map(po => {
                const isOver = po.remainingBalance < 0;
                const usagePct = po.originalAmount > 0 ? (po.amountUsed / po.originalAmount) * 100 : 0;
                return (
                  <Card key={po.id} className={`border-slate-200 shadow-sm rounded-sm ${isOver ? 'border-red-300' : ''}`}>
                    <CardContent className='p-4 md:p-6'>
                      <div className='flex items-center justify-between mb-3'>
                        <div>
                          <h3 className='text-lg font-extrabold text-slate-900'>{po.poNumber}</h3>
                          <p className='text-xs text-slate-500'>{po.linkedJobIds?.length || 0} jobs linked</p>
                        </div>
                      </div>
                      <div className='grid grid-cols-3 gap-3'>
                        <div className='text-center p-3 bg-slate-50 rounded-sm border border-slate-200'>
                          <p className='text-[9px] font-bold uppercase tracking-wider text-slate-500'>Original Amount</p>
                          <p className='text-lg font-extrabold text-slate-900'>${po.originalAmount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div className='text-center p-3 bg-slate-50 rounded-sm border border-slate-200'>
                          <p className='text-[9px] font-bold uppercase tracking-wider text-slate-500'>Previous + Current Usage</p>
                          <p className='text-lg font-extrabold text-amber-700'>${po.amountUsed?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div className={`text-center p-3 rounded-sm border ${isOver ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                          <p className='text-[9px] font-bold uppercase tracking-wider text-slate-500'>Remaining Balance</p>
                          <p className={`text-lg font-extrabold ${isOver ? 'text-red-700' : 'text-green-700'}`}>${po.remainingBalance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        </div>
                      </div>
                      <div className='mt-3'>
                        <div className='w-full h-2 bg-slate-200 rounded-full overflow-hidden'>
                          <div
                            className={`h-full transition-all rounded-full ${usagePct > 100 ? 'bg-red-500' : usagePct > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(usagePct, 100)}%` }}
                          />
                        </div>
                        <p className='text-xs text-slate-500 mt-1 text-right'>{usagePct.toFixed(1)}% of PO used across all jobs</p>
                      </div>
                      {po.jobBreakdown?.length > 0 && (
                        <div className='mt-3 border-t border-slate-200 pt-3'>
                          <p className='text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2'>Per-Job Breakdown</p>
                          {po.jobBreakdown.map((jb, idx) => (
                            <div key={jb.jobNumber || idx} className='flex justify-between text-sm py-1'>
                              <span className='font-medium text-[#1A3668]'>{jb.jobNumber}</span>
                              <span className='font-mono font-bold text-slate-700'>${jb.amountUsed?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })()}
      
      {/* Add/Edit Labor Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='sm:max-w-[800px] rounded-sm max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-2xl font-bold'>
              {editingEntry ? 'Edit Labor Entry' : 'Add Labor Entries'}
            </DialogTitle>
            <DialogDescription>
              {editingEntry 
                ? 'Update labor entry details' 
                : 'Select a craft to see eligible crew, check members, then switch crafts to add more. Assign each person a job, classification, and hours.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEntry}>
            <div className='space-y-4 py-4'>
              {/* Crew Member Info Section */}
              <div className='p-4 bg-slate-50 rounded-sm border border-slate-200'>
                <h4 className='text-sm font-bold uppercase tracking-wider text-slate-700 mb-4'>Crew Member Info</h4>
                
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div>
                    <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                      Craft *
                    </Label>
                    <Select value={currentCraftId} onValueChange={handleCraftChange}>
                      <SelectTrigger data-testid='entry-craft-select' className='mt-2 rounded-sm border-2 bg-white'>
                        <SelectValue placeholder='Select craft' />
                      </SelectTrigger>
                      <SelectContent>
                        {crafts.map((craft) => (
                          <SelectItem key={craft.id} value={craft.id}>
                            {craft.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Classification - only shown for editing single entry */}
                  {editingEntry && (
                    <div>
                      <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                        Classification *
                      </Label>
                      <Select
                        value={currentClassificationId}
                        onValueChange={(value) => setEditFormData({ ...editFormData, classificationId: value })}
                        disabled={!currentCraftId}
                      >
                        <SelectTrigger data-testid='entry-classification-select' className='mt-2 rounded-sm border-2 bg-white'>
                          <SelectValue placeholder='Select classification' />
                        </SelectTrigger>
                        <SelectContent>
                          {getFilteredClassifications(currentCraftId).map((cls) => (
                            <SelectItem key={cls.id} value={cls.id}>
                              {cls.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className={editingEntry ? '' : 'col-span-1 md:col-span-2'}>
                    <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                      {editingEntry ? 'Crew Member *' : 'Select Crew Members'}
                    </Label>
                    {editingEntry ? (
                      <Select
                        value={currentCrewMemberId}
                        onValueChange={(value) => setEditFormData({ ...editFormData, crewMemberId: value })}
                        disabled={!currentCraftId}
                      >
                        <SelectTrigger data-testid='entry-crew-select' className='mt-2 rounded-sm border-2 bg-white'>
                          <SelectValue placeholder='Select crew member' />
                        </SelectTrigger>
                        <SelectContent>
                          {getEligibleCrewMembers(currentCraftId).map((member) => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.fullName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Add Mode: Checkbox Crew Selector */}
              {!editingEntry && currentCraftId && (
                <div className='border border-slate-200 rounded-sm'>
                  <div className='flex items-center justify-between p-3 bg-slate-50 border-b border-slate-200'>
                    <div>
                      <h4 className='text-sm font-bold uppercase tracking-wider text-slate-700'>
                        Select {getCraftName(currentCraftId)} Crew
                      </h4>
                      {crewAllocations.length > 0 && (
                        <p className='text-xs text-slate-500 mt-0.5'>{crewAllocations.length} total selected across all crafts</p>
                      )}
                    </div>
                    <Button type='button' variant='outline' size='sm' onClick={addAllEligibleCrew}
                      className='rounded-sm border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-100 text-xs h-7'
                      data-testid='crew-add-all'>
                      <Users className='w-3.5 h-3.5 mr-1' /> Select All
                    </Button>
                  </div>
                  {/* Search filter */}
                  <div className='px-3 pt-2'>
                    <div className='relative'>
                      <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400' />
                      <Input
                        data-testid='crew-search-filter'
                        value={crewSearchFilter}
                        onChange={(e) => setCrewSearchFilter(e.target.value)}
                        placeholder='Search crew...'
                        className='pl-8 h-8 rounded-sm border-2 text-sm'
                      />
                    </div>
                  </div>
                  <div className='p-2 max-h-48 overflow-y-auto'>
                    {getEligibleCrewMembers(currentCraftId)
                      .filter(m => !crewSearchFilter.trim() || m.fullName.toLowerCase().includes(crewSearchFilter.toLowerCase()))
                      .map((member) => {
                        const isSelected = crewAllocations.some(a => a.crewMemberId === member.id);
                        return (
                          <label
                            key={member.id}
                            data-testid={`crew-checkbox-${member.id}`}
                            className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${isSelected ? 'bg-[#1A3668]/5' : 'hover:bg-slate-50'}`}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleCrewMember(member.id)}
                              className='border-2'
                            />
                            <span className={`text-sm font-medium ${isSelected ? 'text-[#1A3668]' : 'text-slate-700'}`}>
                              {member.fullName}
                            </span>
                            {member.employeeId && (
                              <span className='text-xs text-slate-400 font-mono'>{member.employeeId}</span>
                            )}
                          </label>
                        );
                      })}
                    {getEligibleCrewMembers(currentCraftId).filter(m => !crewSearchFilter.trim() || m.fullName.toLowerCase().includes(crewSearchFilter.toLowerCase())).length === 0 && (
                      <p className='text-sm text-slate-400 p-2 text-center'>No crew members found</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Edit Mode: Job & Hours */}
              {editingEntry && (
                <div className='border border-slate-200 rounded-sm'>
                  <div className='p-4 bg-slate-50 border-b border-slate-200'>
                    <h4 className='text-sm font-bold uppercase tracking-wider text-slate-700'>Job & Hours</h4>
                  </div>
                  <div className='p-4'>
                    <div className='grid grid-cols-4 gap-3 items-end'>
                      <div>
                        <Label className='text-xs font-bold uppercase tracking-wider text-slate-500'>Job *</Label>
                        <Select 
                          value={editFormData.jobId} 
                          onValueChange={(value) => setEditFormData({ ...editFormData, jobId: value })}
                        >
                          <SelectTrigger data-testid='entry-job-select' className='mt-1 rounded-sm border-2'>
                            <SelectValue placeholder='Select job' />
                          </SelectTrigger>
                          <SelectContent>
                            {jobs.map((job) => (
                              <SelectItem key={job.id} value={job.id}>
                                {job.jobNumber} - {job.description}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className='text-xs font-bold uppercase tracking-wider text-slate-500'>ST Hours</Label>
                        <Input
                          data-testid='hours-st-input'
                          type='number'
                          step='0.1'
                          min='0'
                          value={editFormData.hoursST}
                          onChange={(e) => setEditFormData({ ...editFormData, hoursST: e.target.value })}
                          className='mt-1 rounded-sm border-2'
                        />
                      </div>
                      <div>
                        <Label className='text-xs font-bold uppercase tracking-wider text-slate-500'>OT Hours</Label>
                        <Input
                          data-testid='hours-ot-input'
                          type='number'
                          step='0.1'
                          min='0'
                          value={editFormData.hoursOT}
                          onChange={(e) => setEditFormData({ ...editFormData, hoursOT: e.target.value })}
                          className='mt-1 rounded-sm border-2'
                        />
                      </div>
                      <div>
                        <Label className='text-xs font-bold uppercase tracking-wider text-slate-500'>DT Hours</Label>
                        <Input
                          data-testid='hours-dt-input'
                          type='number'
                          step='0.1'
                          min='0'
                          value={editFormData.hoursDT}
                          onChange={(e) => setEditFormData({ ...editFormData, hoursDT: e.target.value })}
                          className='mt-1 rounded-sm border-2'
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Mode: Crew Allocations with Multi-Job */}
              {!editingEntry && crewAllocations.length > 0 && (
                <div className='border border-slate-200 rounded-sm'>
                  <div className='flex items-center justify-between p-4 bg-slate-50 border-b border-slate-200'>
                    <h4 className='text-sm font-bold uppercase tracking-wider text-slate-700'>
                      Crew Allocations ({crewAllocations.length} members, {crewAllocations.reduce((s, a) => s + a.jobs.length, 0)} job rows)
                    </h4>
                    <span className='text-sm text-slate-600'>
                      Total Hours: <strong className='text-[#1A3668]'>{getTotalHours().toFixed(1)}</strong>
                    </span>
                  </div>
                  
                  {/* Quick Apply Row */}
                  <div className='px-4 pt-3 pb-2 border-b border-slate-100 bg-slate-50/50'>
                    <p className='text-xs font-bold uppercase tracking-wider text-slate-400 mb-2'>Apply to all job rows</p>
                    <div className='flex items-center gap-3'>
                      <Select onValueChange={(v) => applyToAllAllocations('jobId', v)}>
                        <SelectTrigger data-testid='apply-all-job' className='rounded-sm border-2 bg-white h-8 text-xs flex-1'>
                          <SelectValue placeholder='Set Job for all...' />
                        </SelectTrigger>
                        <SelectContent>
                          {jobs.map((job) => (
                            <SelectItem key={job.id} value={job.id}>
                              {job.jobNumber} - {job.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className='p-4 space-y-4'>
                    {crewAllocations.map((alloc, allocIndex) => {
                      const member = crewMembers.find(m => m.id === alloc.crewMemberId);
                      return (
                        <div key={alloc.crewMemberId} data-testid={`crew-alloc-row-${allocIndex}`} className='border border-slate-200 rounded-sm'>
                          <div className='flex items-center justify-between p-3 bg-slate-50/50 border-b border-slate-100'>
                            <div className='flex items-center gap-2'>
                              <span className='font-bold text-[#1A3668] text-sm'>{member?.fullName || 'Unknown'}</span>
                              <Badge className='bg-[#1A3668]/10 text-[#1A3668] border-[#1A3668]/20 rounded-sm text-xs'>
                                {getCraftName(alloc.craftId)}
                              </Badge>
                            </div>
                            <div className='flex items-center gap-2'>
                              <Button type='button' onClick={() => addJobRow(allocIndex)} variant='outline' size='sm'
                                data-testid={`add-job-row-${allocIndex}`}
                                className='rounded-sm border-slate-200 text-[#1A3668] hover:bg-slate-100 h-7 text-xs'>
                                <Plus className='w-3 h-3 mr-1' /> Add Job
                              </Button>
                              <Button type='button' onClick={() => removeCrewAllocation(allocIndex)} variant='ghost' size='sm' 
                                data-testid={`remove-crew-alloc-${allocIndex}`}
                                className='text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0'>
                                <X className='w-4 h-4' />
                              </Button>
                            </div>
                          </div>
                          <div className='p-3 space-y-2'>
                            {alloc.jobs.map((job, jobIndex) => {
                              return (
                              <div key={job.jobId || jobIndex}>
                              <div data-testid={`crew-alloc-${allocIndex}-job-${jobIndex}`} className='grid grid-cols-2 md:grid-cols-6 gap-2 items-end'>
                                <div>
                                  <Label className='text-xs font-bold uppercase text-slate-500'>Job *</Label>
                                  <Select value={job.jobId} onValueChange={(v) => updateJobRow(allocIndex, jobIndex, 'jobId', v)}>
                                    <SelectTrigger data-testid={`crew-alloc-job-${allocIndex}-${jobIndex}`} className='mt-1 rounded-sm border-2 h-9 text-xs'>
                                      <SelectValue placeholder='Job' />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {jobs.map(j => (
                                        <SelectItem key={j.id} value={j.id}>{j.jobNumber}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className='text-xs font-bold uppercase text-slate-500'>Class *</Label>
                                  <Select value={job.classificationId} onValueChange={(v) => updateJobRow(allocIndex, jobIndex, 'classificationId', v)} disabled={!alloc.craftId}>
                                    <SelectTrigger data-testid={`crew-alloc-class-${allocIndex}-${jobIndex}`} className='mt-1 rounded-sm border-2 h-9 text-xs'>
                                      <SelectValue placeholder='Class' />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {getFilteredClassifications(alloc.craftId).map(cls => (
                                        <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className='text-xs font-bold uppercase text-slate-500'>ST Hrs</Label>
                                  <Input data-testid={`crew-alloc-st-${allocIndex}-${jobIndex}`} type='number' step='0.1' min='0' value={job.hoursST}
                                    onChange={(e) => updateJobRow(allocIndex, jobIndex, 'hoursST', e.target.value)} className='mt-1 rounded-sm border-2 h-9 text-xs' />
                                </div>
                                <div>
                                  <Label className='text-xs font-bold uppercase text-slate-500'>OT Hrs</Label>
                                  <Input data-testid={`crew-alloc-ot-${allocIndex}-${jobIndex}`} type='number' step='0.1' min='0' value={job.hoursOT}
                                    onChange={(e) => updateJobRow(allocIndex, jobIndex, 'hoursOT', e.target.value)} className='mt-1 rounded-sm border-2 h-9 text-xs' />
                                </div>
                                <div>
                                  <Label className='text-xs font-bold uppercase text-slate-500'>DT Hrs</Label>
                                  <Input data-testid={`crew-alloc-dt-${allocIndex}-${jobIndex}`} type='number' step='0.1' min='0' value={job.hoursDT}
                                    onChange={(e) => updateJobRow(allocIndex, jobIndex, 'hoursDT', e.target.value)} className='mt-1 rounded-sm border-2 h-9 text-xs' />
                                </div>
                                <div className='flex items-end'>
                                  {alloc.jobs.length > 1 && (
                                    <Button type='button' onClick={() => removeJobRow(allocIndex, jobIndex)} variant='ghost' size='sm'
                                      data-testid={`remove-job-row-${allocIndex}-${jobIndex}`}
                                      className='text-red-400 hover:text-red-600 hover:bg-red-50 h-9 w-9 p-0 mt-1'>
                                      <Trash2 className='w-3.5 h-3.5' />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty state when no crew members added yet (add mode only) */}
              {!editingEntry && crewAllocations.length === 0 && formData.craftId && (
                <div className='border border-dashed border-slate-300 rounded-sm p-6 text-center'>
                  <Users className='w-8 h-8 mx-auto text-slate-400 mb-2' />
                  <p className='text-sm text-slate-500'>Check crew members above to assign hours</p>
                </div>
              )}
              
              {/* Notes */}
              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                  Notes (Optional)
                </Label>
                <Input
                  data-testid='entry-notes-input'
                  value={editingEntry ? editFormData.notes : formData.notes}
                  onChange={(e) => editingEntry 
                    ? setEditFormData({ ...editFormData, notes: e.target.value })
                    : setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder='Optional notes'
                  className='mt-2 rounded-sm border-2'
                />
              </div>
            </div>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => setDialogOpen(false)} className='border-2 border-slate-200 rounded-sm'>
                Cancel
              </Button>
              <Button
                type='submit'
                data-testid='submit-labor-entry-button'
                disabled={submitting}
                className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm'
              >
                {submitting ? 'Saving...' : editingEntry ? 'Update' : `Add ${crewAllocations.reduce((count, a) => count + a.jobs.filter(j => j.jobId && j.classificationId && ((parseFloat(j.hoursST) || 0) + (parseFloat(j.hoursOT) || 0) + (parseFloat(j.hoursDT) || 0)) > 0).length, 0) || ''} Entries`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className='rounded-sm'>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Labor Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this labor entry? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='rounded-sm'>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEntry}
              data-testid='confirm-delete-entry'
              className='bg-red-600 hover:bg-red-700 rounded-sm'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Move Labor Entry Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className='sm:max-w-[500px] rounded-sm'>
          <DialogHeader>
            <DialogTitle className='text-2xl font-bold'>Move Labor Entry</DialogTitle>
            <DialogDescription>
              Move this labor entry to a different job. A reason is required for audit purposes.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div>
              <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                New Job *
              </Label>
              <Select value={moveData.newJobId} onValueChange={(value) => setMoveData({ ...moveData, newJobId: value })}>
                <SelectTrigger data-testid='move-job-select' className='mt-2 rounded-sm border-2'>
                  <SelectValue placeholder='Select new job' />
                </SelectTrigger>
                <SelectContent>
                  {jobs.filter(j => j.id !== movingEntry?.jobId).map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.jobNumber} - {job.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                Reason *
              </Label>
              <Input
                data-testid='move-reason-input'
                value={moveData.reason}
                onChange={(e) => setMoveData({ ...moveData, reason: e.target.value })}
                placeholder='Enter reason for move'
                required
                className='mt-2 rounded-sm border-2'
              />
            </div>
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => setMoveDialogOpen(false)} className='border-2 border-slate-200 rounded-sm'>
              Cancel
            </Button>
            <Button
              onClick={handleMoveEntry}
              data-testid='confirm-move-entry'
              disabled={!moveData.newJobId || !moveData.reason}
              className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm'
            >
              Move Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Submit Timesheet Dialog */}
      <AlertDialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <AlertDialogContent className='rounded-sm'>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Timesheet</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to submit this timesheet for approval? Once submitted, you will not be able to make changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='rounded-sm'>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmitTimesheet}
              data-testid='confirm-submit-timesheet'
              className='bg-amber-500 hover:bg-amber-600 rounded-sm'
            >
              Submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Approve Timesheet Dialog */}
      <AlertDialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <AlertDialogContent className='rounded-sm'>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Timesheet</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to approve this timesheet? This will finalize all labor entries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='rounded-sm'>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApproveTimesheet}
              data-testid='confirm-approve-timesheet'
              className='bg-green-600 hover:bg-green-700 rounded-sm'
            >
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Equipment Entry Dialog */}
      <Dialog open={equipDialogOpen} onOpenChange={setEquipDialogOpen}>
        <DialogContent className='sm:max-w-[500px] rounded-sm'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold flex items-center gap-2'>
              <Truck className='w-5 h-5 text-amber-600' />
              {editingEquipEntry ? 'Edit Equipment Entry' : 'Add Equipment Entry'}
            </DialogTitle>
            <DialogDescription>
              {editingEquipEntry ? 'Update equipment usage' : 'Log company-owned equipment usage for this timesheet'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEquipSubmit}>
            <div className='space-y-4 py-4'>
              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                  Equipment *
                </Label>
                <Select value={equipFormData.equipmentId} onValueChange={(v) => {
                  const equip = internalEquipment.find(e => e.id === v);
                  setEquipFormData({
                    ...equipFormData,
                    equipmentId: v,
                    hourlyRate: equip ? equip.hourlyRate.toString() : ''
                  });
                }}>
                  <SelectTrigger data-testid='equip-entry-equipment-select' className='mt-2 rounded-sm border-2 bg-white'>
                    <SelectValue placeholder='Select equipment' />
                  </SelectTrigger>
                  <SelectContent>
                    {internalEquipment.map((equip) => (
                      <SelectItem key={equip.id} value={equip.id}>
                        {equip.name} (${equip.hourlyRate}/hr)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                  Job *
                </Label>
                <Select value={equipFormData.jobId} onValueChange={(v) => setEquipFormData({...equipFormData, jobId: v})}>
                  <SelectTrigger data-testid='equip-entry-job-select' className='mt-2 rounded-sm border-2 bg-white'>
                    <SelectValue placeholder='Select job' />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.filter(j => j.status === 'ACTIVE').map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.jobNumber} - {job.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                  Hourly Rate ($) *
                </Label>
                <div className='relative mt-2'>
                  <span className='absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold'>$</span>
                  <Input
                    type='number'
                    step='0.01'
                    min='0'
                    data-testid='equip-entry-rate-input'
                    value={equipFormData.hourlyRate}
                    onChange={(e) => setEquipFormData({...equipFormData, hourlyRate: e.target.value})}
                    placeholder='0.00'
                    required
                    className='rounded-sm border-2 pl-7'
                  />
                </div>
                {equipFormData.equipmentId && (() => {
                  const equip = internalEquipment.find(e => e.id === equipFormData.equipmentId);
                  if (equip && equipFormData.hourlyRate && parseFloat(equipFormData.hourlyRate) !== equip.hourlyRate) {
                    return (
                      <p className='text-xs text-amber-600 mt-1 font-medium'>
                        Default rate: ${equip.hourlyRate}/hr — manually overridden
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                  Hours *
                </Label>
                <Input
                  type='number'
                  step='0.1'
                  min='0'
                  data-testid='equip-entry-hours-input'
                  value={equipFormData.hours}
                  onChange={(e) => setEquipFormData({...equipFormData, hours: e.target.value})}
                  placeholder='8'
                  required
                  className='mt-2 rounded-sm border-2'
                />
              </div>
              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                  Notes
                </Label>
                <Input
                  data-testid='equip-entry-notes-input'
                  value={equipFormData.notes}
                  onChange={(e) => setEquipFormData({...equipFormData, notes: e.target.value})}
                  placeholder='Optional notes about equipment usage'
                  className='mt-2 rounded-sm border-2'
                />
              </div>
              {equipFormData.hourlyRate && equipFormData.hours && (
                <div className='bg-slate-50 border-2 border-slate-200 rounded-sm p-3'>
                  <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Estimated Total</p>
                  <p className='text-xl font-bold text-slate-900 mt-1' data-testid='equip-entry-estimated-total'>
                    ${(parseFloat(equipFormData.hourlyRate) * parseFloat(equipFormData.hours)).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => setEquipDialogOpen(false)} className='border-2 border-slate-200 rounded-sm'>
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={equipSubmitting || !equipFormData.equipmentId || !equipFormData.jobId || !equipFormData.hours || !equipFormData.hourlyRate}
                data-testid='save-equipment-entry-button'
                className='bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-sm'
              >
                {equipSubmitting ? 'Saving...' : editingEquipEntry ? 'Update' : 'Add Entry'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Equipment Entry Dialog */}
      <AlertDialog open={deleteEquipDialogOpen} onOpenChange={setDeleteEquipDialogOpen}>
        <AlertDialogContent className='rounded-sm'>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Equipment Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this equipment entry for <strong>{deletingEquipEntry?.equipmentName}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='rounded-sm'>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEquipEntry}
              data-testid='confirm-delete-equipment-entry'
              className='bg-red-600 hover:bg-red-700 rounded-sm'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
